import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraEmptyState, SpecraInlineBanner, SpecraListPage, SpecraPageHeader, SpecraPagination, SpecraTabBar } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useRecordShellEnabled } from '../../components/RecordShell/recordShellFlag';
import { useRecordBreadcrumbs } from '../../components/RecordShell/useRecordBreadcrumbs';
import { useFeatureFlagsReady } from '../../contexts/featureFlagContext';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { growthCrumbs } from '../GrowthHub/growthCrumbs';
import { useGrowthWorkspaces } from './useGrowthWorkspaces';
import { WorkspaceRows } from './WorkspaceTable';
const COPY = {
    rfi_response: {
        title: 'RFI responses',
        description: 'Workspaces for answering RFIs and sources sought, question by question.',
        pageTestId: Testids.growthWorkspaces.rfiListPage,
        emptyOpen: 'No open RFI responses',
        emptyClosed: 'No closed RFI responses'
    },
    proposal: {
        title: 'Proposals',
        description: 'RFP workspaces: compliance, volumes, reviews and pricing.',
        pageTestId: Testids.growthWorkspaces.proposalListPage,
        emptyOpen: 'No open proposals',
        emptyClosed: 'No closed proposals'
    }
};
/**
 * GRW-02: the Growth-side list of RFI-response or Proposal workspaces that
 * links into them (WRT/PRP own the workspaces, D9). Behind
 * growth_record_shell_v1; off redirects to the hub.
 */ export function GrowthWorkspaceListPage({ kind, actions }) {
    const ready = useFeatureFlagsReady();
    const shell = useRecordShellEnabled();
    const copy = COPY[kind];
    const [status, setStatus] = useState('open');
    // Token stack: tokens[i] fetches page i + 1.
    const [tokens, setTokens] = useState([
        ''
    ]);
    const page = tokens.length;
    const query = useGrowthWorkspaces({
        kind,
        status,
        pageToken: tokens[page - 1] ?? '',
        enabled: shell
    });
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    useRecordBreadcrumbs(shell ? growthCrumbs({
        label: copy.title
    }) : null);
    if (ready && !shell) return /*#__PURE__*/ _jsx(Navigate, {
        to: "/growth",
        replace: true
    });
    const data = query.data;
    const next = data?.nextPageToken ?? '';
    const changeStatus = (value)=>{
        setStatus(value);
        setTokens([
            ''
        ]);
    };
    const error = query.isError ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "danger",
        variant: "framed",
        "data-testid": Testids.growthWorkspaces.error,
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            variant: "secondary",
            size: "sm",
            onClick: ()=>query.refetch(),
            children: "Retry"
        }),
        children: [
            copy.title,
            " could not be loaded."
        ]
    }) : undefined;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": copy.pageTestId,
        children: /*#__PURE__*/ _jsx(SpecraListPage, {
            padding: "page",
            header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                titleSize: "ui",
                title: copy.title,
                description: copy.description,
                actions: actions
            }),
            toolbar: /*#__PURE__*/ _jsx(SpecraTabBar, {
                label: `${copy.title} by status`,
                value: status,
                onValueChange: changeStatus,
                options: [
                    {
                        value: 'open',
                        label: 'Open',
                        count: data?.openCount
                    },
                    {
                        value: 'closed',
                        label: 'Closed',
                        count: data?.closedCount
                    }
                ]
            }),
            footer: page > 1 || next ? /*#__PURE__*/ _jsx(SpecraPagination, {
                page: page,
                pageCount: next ? page + 1 : page,
                itemLabel: copy.title.toLowerCase(),
                onPageChange: (p)=>{
                    if (p > page && next) setTokens([
                        ...tokens,
                        next
                    ]);
                    else if (p < page) setTokens(tokens.slice(0, p));
                }
            }) : undefined,
            children: /*#__PURE__*/ _jsx(WorkspaceRows, {
                rows: data?.rows ?? [],
                loading: query.isLoading,
                dueColumn: kind === 'rfi_response',
                error: error,
                empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    "data-testid": Testids.growthWorkspaces.empty,
                    title: status === 'open' ? copy.emptyOpen : copy.emptyClosed,
                    description: "Only workspaces you can open are listed."
                })
            })
        })
    });
}
